import { useEffect, useMemo, useState, useCallback } from 'react';
import { apiFetch } from '../services/apiClient';
import { AlertTriangle, CheckCircle2, Clock, FileText, Pause, Play, RefreshCw, Server, Terminal, Activity, History, FlaskConical, Workflow, Watch } from 'lucide-react';
import { AgentTracePanel } from './AgentTracePanel';
import { CheckpointPanel } from './CheckpointPanel';
import { EvaluationPanel } from './EvaluationPanel';
import { WorkflowEditor } from './WorkflowEditor';
import { WearableDevicePanel } from './WearableDevicePanel';

interface RuntimeLogSource {
  id: string;
  path: string;
  name: string;
  modifiedAt: string;
  size: number;
  lines: string[];
}

interface RuntimeLogPayload {
  runtime: {
    version?: string;
    pid?: number;
    startedAt?: string;
    uptimeSeconds?: number;
    节点Version?: string;
    platform?: string;
  };
  generatedAt: string;
  sources: RuntimeLogSource[];
}

const formatBytes = (bytes: number) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let index = 0;
  while (value >= 1024 && index < units.length - 1) {
    value /= 1024;
    index += 1;
  }
  return `${value.toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
};

const formatUptime = (seconds?: number) => {
  if (!seconds || seconds < 0) return '0s';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

const classifyLine = (line: string) => {
  if (/\b(error|failed|fatal|exception|crash)\b/i.test(line)) return 'text-red-300 bg-red-500/10 border-red-500/20';
  if (/\b(warn|skipped|degraded|missing)\b/i.test(line)) return 'text-amber-200 bg-amber-500/10 border-amber-500/20';
  if (/\b(ready|running|connected|success|registered|discovered)\b/i.test(line)) return 'text-emerald-200 bg-emerald-500/10 border-emerald-500/20';
  if (/^\[(API_ROUTER|Socket|MCP|Scheduler|INFO)\]/.test(line)) return 'text-cyan-100/80 bg-cyan-500/5 border-cyan-500/10';
  return 'text-white/55 border-white/5';
};

export function RuntimeLogPanel({ t }: { t: any }) {
  const [view, setView] = useState<'logs' | 'trace' | 'checkpoint' | 'evaluation' | 'workflow' | 'wearable'>('logs');
  const [payload, setPayload] = useState<RuntimeLogPayload | null>(null);
  const [activeSourceId, setActiveSourceId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);

  const loadLogs = useCallback(async () => {
    setError('');
    try {
      const res = await apiFetch('/api/runtime/logs?lines=320', { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Failed to load logs (${res.status})`);
      setPayload(data);
      setActiveSourceId(prev => prev || 'all');
    } catch (err: any) {
      setError(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  }, []);;

  useEffect(() => {
    void loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    if (!autoRefresh) return;
    const timer = window.setInterval(() => void loadLogs(), 5000);
    return () => window.clearInterval(timer);
  }, [autoRefresh]);

  const sources = payload?.sources || [];
  const activeSource = useMemo(
    () => sources.find(source => source.id === activeSourceId) || sources[0],
    [activeSourceId, sources],
  );
  const visibleLines = activeSource?.lines || [];
  const issueCount = visibleLines.filter(line => /\b(error|failed|fatal|exception|crash|warn)\b/i.test(line)).length;

  return (
    <div className="flex h-full min-h-0 flex-col bg-zinc-950 text-white">
      <div className="shrink-0 border-b border-white/10 bg-white/[0.03] px-5 py-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.18em] text-cyan-300">
              <Terminal size={15} />
              {t?.runtimeLog || '运行日志'}
            </div>
            {/* P0-A: 日志 / Agent 追踪 切换 */}
            <div className="mt-2 flex items-center gap-1 rounded-lg border border-white/10 bg-white/[0.03] p-1">
              <button
                type="button"
                onClick={() => setView('logs')}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[11px] font-bold transition-colors ${view === 'logs' ? 'bg-cyan-400/15 text-cyan-200' : 'text-white/45 hover:text-white'}`}
              >
                <FileText size={12} /> {t?.logsTab || '日志'}
              </button>
              <button
                type="button"
                onClick={() => setView('trace')}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[11px] font-bold transition-colors ${view === 'trace' ? 'bg-cyan-400/15 text-cyan-200' : 'text-white/45 hover:text-white'}`}
              >
                <Activity size={12} /> {t?.traceTab || 'Agent 追踪'}
              </button>
              <button
                type="button"
                onClick={() => setView('checkpoint')}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[11px] font-bold transition-colors ${view === 'checkpoint' ? 'bg-cyan-400/15 text-cyan-200' : 'text-white/45 hover:text-white'}`}
              >
                <History size={12} /> {t?.checkpointTab || '断点续跑'}
              </button>
              <button
                type="button"
                onClick={() => setView('evaluation')}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[11px] font-bold transition-colors ${view === 'evaluation' ? 'bg-cyan-400/15 text-cyan-200' : 'text-white/45 hover:text-white'}`}
              >
                <FlaskConical size={12} /> {t?.evaluationTab || '评测'}
              </button>
              <button
                type="button"
                onClick={() => setView('workflow')}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[11px] font-bold transition-colors ${view === 'workflow' ? 'bg-cyan-400/15 text-cyan-200' : 'text-white/45 hover:text-white'}`}
              >
                <Workflow size={12} /> {t?.workflowTab || '编排'}
              </button>
              <button
                type="button"
                onClick={() => setView('wearable')}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[11px] font-bold transition-colors ${view === 'wearable' ? 'bg-cyan-400/15 text-cyan-200' : 'text-white/45 hover:text-white'}`}
              >
                <Watch size={12} /> {t?.wearableTab || '设备'}
              </button>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-white/45">
              <span className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/5 px-2 py-1">
                <Server size={12} /> PID {payload?.runtime?.pid || '-'}
              </span>
              <span className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/5 px-2 py-1">
                <Clock size={12} /> {formatUptime(payload?.runtime?.uptimeSeconds)}
              </span>
              <span className="rounded-md border border-white/10 bg-white/5 px-2 py-1">
                {payload?.runtime?.节点Version || '节点'}
              </span>
              {issueCount > 0 ? (
                <span className="inline-flex items-center gap-1 rounded-md border border-amber-400/20 bg-amber-400/10 px-2 py-1 text-amber-200">
                  <AlertTriangle size={12} /> {issueCount}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-md border border-emerald-400/20 bg-emerald-400/10 px-2 py-1 text-emerald-200">
                  <CheckCircle2 size={12} /> clean
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setAutoRefresh(prev => !prev)}
              className={`inline-flex h-9 w-9 items-center justify-center rounded-lg border transition-colors ${autoRefresh ? 'border-cyan-400/30 bg-cyan-400/10 text-cyan-200' : 'border-white/10 bg-white/5 text-white/45 hover:text-white'}`}
              title={autoRefresh ? t.pauseRefresh || '暂停刷新' : t.resumeRefresh || '继续刷新'}
            >
              {autoRefresh ? <Pause size={15} /> : <Play size={15} />}
            </button>
            <button
              type="button"
              onClick={() => void loadLogs()}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-white/55 transition-colors hover:bg-white/10 hover:text-white"
              title={t?.refresh || '刷新'}
            >
              <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
      </div>

      {view === 'trace' ? (
        <div className="min-h-0 flex-1">
          <AgentTracePanel t={t} />
        </div>
      ) : view === 'checkpoint' ? (
        <div className="min-h-0 flex-1">
          <CheckpointPanel t={t} />
        </div>
      ) : view === 'evaluation' ? (
        <div className="min-h-0 flex-1">
          <EvaluationPanel t={t} />
        </div>
      ) : view === 'workflow' ? (
        <div className="min-h-0 flex-1">
          <WorkflowEditor t={t} />
        </div>
      ) : view === 'wearable' ? (
        <div className="min-h-0 flex-1">
          <WearableDevicePanel t={t} />
        </div>
      ) : (
      <div className="grid min-h-0 flex-1 grid-cols-[240px_1fr]">
        <aside className="min-h-0 overflow-y-auto border-r border-white/10 bg-black/20 p-3 custom-scrollbar">
          {sources.length === 0 && (
            <div className="rounded-lg border border-dashed border-white/10 px-3 py-8 text-center text-xs text-white/35">
              {loading ? t.loadingLogs || '加载日志中...' : t.noLogsFound || '暂无运行日志'}
            </div>
          )}
          <div className="space-y-2">
            {sources.map(source => {
              const active = source.id === activeSource?.id;
              return (
                <button
                  type="button"
                  key={source.id}
                  onClick={() => setActiveSourceId(source.id)}
                  className={`w-full rounded-lg border px-3 py-2 text-left transition-colors ${active ? 'border-cyan-400/30 bg-cyan-400/10' : 'border-white/8 bg-white/[0.03] hover:bg-white/[0.06]'}`}
                >
                  <div className="flex items-center gap-2 text-xs font-bold text-white/80">
                    <FileText size={13} className={active ? 'text-cyan-200' : 'text-white/35'} />
                    <span className="truncate">{source.name}</span>
                  </div>
                  <div className="mt-1 truncate text-[10px] text-white/35">{source.path}</div>
                  <div className="mt-1 flex items-center justify-between text-[10px] text-white/30">
                    <span>{formatBytes(source.size)}</span>
                    <span>{source.lines.length} lines</span>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        <section className="min-h-0 overflow-hidden">
          {error ? (
            <div className="m-5 rounded-lg border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-200">{error}</div>
          ) : (
            <div className="h-full overflow-y-auto p-4 font-mono text-[11px] leading-relaxed custom-scrollbar">
              {visibleLines.length === 0 ? (
                <div className="flex h-full items-center justify-center text-sm text-white/35">
                  {loading ? t.loadingLogs || '加载日志中...' : t.logEmpty || '此日志为空'}
                </div>
              ) : (
                visibleLines.map((line, index) => (
                  <div
                    key={`${activeSource?.id || 'log'}-${index}`}
                    className={`mb-1 rounded-md border px-2 py-1 ${classifyLine(line)}`}
                  >
                    <span className="mr-3 select-none text-white/20">{String(index + 1).padStart(3, '0')}</span>
                    <span className="whitespace-pre-wrap break-words">{line}</span>
                  </div>
                ))
              )}
            </div>
          )}
        </section>
      </div>
      )}
    </div>
  );
}
